import dayjs from "dayjs";

const SUNDAY = 0;

/**
 * Compute the cash discount (Skonto) deadline from the invoice arrival date.
 *
 * Calendar-based: deadline = arrivalDate + 1 + cashDiscountDays calendar days.
 * The construction industry treats Saturday as a working day, so only Sundays
 * are rolled forward - when the deadline lands on a Sunday it moves to Monday.
 *
 * NOTE: public holidays are NOT handled yet. German Feiertage are per-Bundesland,
 * so this needs a holiday source (e.g. the `date-holidays` package) plus the
 * project's state before a holiday roll-forward can be added here.
 *
 * @param {Date|string} baseDate - invoice arrival date the skonto period runs from
 * @param {number|string} cashDiscountDays
 * @returns {Date|null}
 */
export const computeCashDiscountDeadline = (baseDate, cashDiscountDays) => {
	if (!baseDate || cashDiscountDays == null) return null;

	const parsedDays = Number(cashDiscountDays);
	if (!Number.isFinite(parsedDays)) return null;

	const start = dayjs(baseDate);
	if (!start.isValid()) return null;

	let result = start.add(1 + Math.max(0, Math.floor(parsedDays)), "day");

	// Saturday counts as a working day; only roll a Sunday forward to Monday.
	if (result.day() === SUNDAY) {
		result = result.add(1, "day");
	}

	return result.toDate();
};

/**
 * Resolve cash discount date from explicit selection or the computed deadline.
 * The base date is the invoice arrival date (Rechnungseingang).
 * @param {Date|string|null} cashDiscountDate
 * @param {Date|string|null} baseDate - invoice arrival date the skonto period runs from
 * @param {number|string|null} cashDiscountDays
 * @returns {Date|null}
 */
export const resolveCashDiscountDate = (
	cashDiscountDate,
	baseDate,
	cashDiscountDays,
) => {
	if (cashDiscountDate) return cashDiscountDate;
	return computeCashDiscountDeadline(baseDate, cashDiscountDays);
};

/**
 * Prepares invoice data for submission to the backend
 * @param {Object} invoiceData - The invoice form data to prepare
 * @param {Map} invoicePositions - Position data Map with structured format
 * @returns {Object} The prepared invoice data ready for submission
 */
export const prepareInvoiceV2DataForSubmission = (
	invoiceData,
	invoicePositions,
) => {
	const {
		id,
		invoiceNumber,
		invoiceDate,
		invoiceArrivalDate,
		cashDiscountDate,
		billingPeriodFrom,
		billingPeriodTo,
		isFinalInvoice,
		salesTaxPercentage,
		selectedTicketCategory,
		orderNumber,
		orderDate,
		isHistoricalBaseline,
		historicalInvoices,
	} = invoiceData;

	let positions = [];

	// Use invoice positions if available - updated for new structure
	if (invoicePositions && invoicePositions.size > 0) {
		positions = Array.from(invoicePositions.entries())
			.filter(([positionId, positionData]) => {
				return positionData?.invoice?.quantity > 0;
			})
			.map(([positionId, positionData]) => {
				const quantity = positionData.invoice?.quantity || 0;
				const aufmassQuantity =
					positionData.lastAllocation?.aufmassQuantity || 0;
				const fromPreviousInvoice =
					positionData.fromPreviousInvoice || false;
				const comments = positionData.invoice?.comments || "";

				return {
					positionId: positionId,
					quantity: quantity,
					comments: comments,
					fromPreviousInvoice: fromPreviousInvoice,
					originalQuantity: aufmassQuantity,
				};
			});
	}

	// Prepare the payload
	const payload = {
		id,
		invoiceNumber,
		invoiceDate,
		invoiceArrivalDate: invoiceArrivalDate || null,
		// Send the stored deadline as-is. It is auto-derived from the arrival
		// date at edit time (handleArrivalDateChange / syncAutoCashDiscountDate),
		// but the user may clear or override it - so the saved value must be the
		// single source of truth, never re-derived here.
		cashDiscountDate: cashDiscountDate || null,
		projectTicketCategoryId: selectedTicketCategory?.value,
		billingPeriodFrom,
		billingPeriodTo,
		isFinalInvoice: isFinalInvoice || false,
		salesTaxPercentage: salesTaxPercentage || 0,
		positions,
		orderNumber,
		orderDate,
		isHistoricalBaseline: isHistoricalBaseline || false,
		historicalInvoices: isHistoricalBaseline
			? historicalInvoices
			: undefined,
	};

	return payload;
};

/**
 * Common V2 invoice form validation for both autosave and save
 * Checks all required fields, ticket category, positions, and business rules
 * @param {Object} formData - The invoice form data
 * @param {Map} invoicePositions - Position data Map
 * @param {boolean} isDraft - Whether this is a draft (less strict validation)
 * @returns {Object} - Object with isValid flag and errors object
 */
export const validateInvoiceV2FormData = (
	formData,
	invoicePositions,
	ticketCategoryDetails,
) => {
	const {
		invoiceNumber,
		invoiceDate,
		billingPeriodFrom,
		billingPeriodTo,
		selectedTicketCategory,
		invoiceArrivalDate,
		cashDiscountDate,
		readonly,
	} = formData;

	let isValid = true;
	const errors = {};

	// Check ticket category
	if (!selectedTicketCategory || !selectedTicketCategory.value) {
		errors.selectedTicketCategory =
			"invoice.error-ticket-category-required";
		isValid = false;
	}

	// Check positions
	const hasPositionsWithQuantities =
		invoicePositions &&
		invoicePositions.size > 0 &&
		Array.from(invoicePositions.values()).some((positionData) => {
			return positionData?.invoice?.quantity > 0;
		});
	if (!hasPositionsWithQuantities) {
		errors.positions = "invoice.error-draft-minimum-requirements";
		isValid = false;
	}

	// Check for position quantity business rules
	const positionValidation = validatePositionQuantities(invoicePositions);
	if (positionValidation.hasErrors) {
		errors.positionQuantities = positionValidation.errors;
		isValid = false;
	}

	// If not draft, check all required fields
	if (!invoiceNumber) {
		errors.invoiceNumber = "invoice.error-invoice-number-required";
		isValid = false;
	}
	if (!invoiceDate) {
		errors.invoiceDate = "invoice.error-invoice-date-required";
		isValid = false;
	}
	if (!billingPeriodFrom) {
		errors.billingPeriodFrom = "invoice.error-billing-period-from-required";
		isValid = false;
	}
	if (!billingPeriodTo) {
		errors.billingPeriodTo = "invoice.error-billing-period-to-required";
		isValid = false;
	}
	if (
		billingPeriodTo &&
		billingPeriodFrom &&
		billingPeriodTo < billingPeriodFrom
	) {
		errors.billingPeriodTo =
			"invoice.error-billing-period-to-less-than-from";
		isValid = false;
	}
	if (invoiceDate && billingPeriodFrom && invoiceDate < billingPeriodFrom) {
		errors.invoiceDate =
			"invoice.error-invoice-date-greater-than-billing-period-from";
		isValid = false;
	}

	// Cash discount (skonto): when the selected category defines a cash
	// discount, both the invoice arrival date (Rechnungseingang) and the
	// resulting cash-discount deadline are mandatory. Without a cash discount
	// they stay optional.
	if (Number(ticketCategoryDetails?.cashDiscount) > 0) {
		if (!invoiceArrivalDate) {
			errors.invoiceArrivalDate =
				"invoice.error-invoice-arrival-date-required";
			isValid = false;
		}
		if (!cashDiscountDate) {
			errors.cashDiscountDate =
				"invoice.error-cash-discount-date-required";
			isValid = false;
		}
	}

	// Optionally block if readonly
	if (readonly) {
		errors.readonly = "invoice.error-readonly";
		isValid = false;
	}

	return { isValid, errors };
};

/** Field order for save-validation toasts (most visible form fields first). */
const INVOICE_VALIDATION_ERROR_PRIORITY = [
	"invoiceNumber",
	"invoiceDate",
	"billingPeriodFrom",
	"billingPeriodTo",
	"selectedTicketCategory",
	"invoiceArrivalDate",
	"cashDiscountDate",
	"positions",
	"readonly",
];

/**
 * Returns the i18n key for the most relevant validation error toast.
 * @param {Object} errors - Errors object from validateInvoiceV2FormData
 * @returns {string} Translation key
 */
export const getInvoiceV2ValidationToastKey = (errors) => {
	if (!errors || typeof errors !== "object") {
		return "invoice.error-validation-failed";
	}

	for (const field of INVOICE_VALIDATION_ERROR_PRIORITY) {
		const key = errors[field];
		if (typeof key === "string" && key.length > 0) {
			return key;
		}
	}

	if (errors.positionQuantities) {
		return "invoice.error-position-quantities-invalid";
	}

	return "invoice.error-validation-failed";
};

/**
 * Validates position quantities against minimum requirements
 * @param {Map} invoicePositions - Position data Map from V2 system with structured format
 * @returns {Object} - Object with hasErrors flag and errors array
 */
export const validatePositionQuantities = (invoicePositions) => {
	const errors = [];
	let hasErrors = false;

	if (invoicePositions && invoicePositions.size > 0) {
		Array.from(invoicePositions.entries()).forEach(
			([positionId, positionData]) => {
				const quantity = positionData?.invoice?.quantity || 0;
				const minimumAllowed =
					positionData?.validations?.minAllowed || 0;

				// Check if quantity is below minimum allowed - fixed to include 0 values
				if (minimumAllowed > 0 && quantity < minimumAllowed) {
					hasErrors = true;
					errors.push({
						positionId,
						type: "minimum_quantity",
						message: `Position ${positionId} quantity ${quantity} is below minimum required ${minimumAllowed}`,
						quantity,
						minimumAllowed,
					});
				}
			},
		);
	}

	return { hasErrors, errors };
};

/**
 * Checks if the V2 form has sufficient data for autosave
 * @param {Object} formData - The V2 invoice form data
 * @param {Map} positionQuantities - Position quantities Map from V2 system
 * @returns {boolean} Whether autosave should be attempted
 */
export const canAutosaveV2Invoice = (formData, invoicePositions) => {
	const {
		selectedTicketCategory,
		readonly,
		invoiceNumber,
		invoiceDate,
		billingPeriodFrom,
		billingPeriodTo,
	} = formData;

	const hasPositionsWithQuantities =
		invoicePositions &&
		invoicePositions.size > 0 &&
		Array.from(invoicePositions.values()).some((positionData) => {
			return positionData?.invoice?.quantity > 0;
		});

	// Enhanced autosave validation - check for basic invoice details
	// This prevents autosaving incomplete invoices that would fail on submission
	const hasBasicInvoiceDetails = !!(
		invoiceNumber &&
		invoiceDate &&
		billingPeriodFrom &&
		billingPeriodTo
	);

	// Check for position validation errors (e.g., minimum quantity violations)
	const positionValidation = validatePositionQuantities(invoicePositions);

	// For autosave, we need:
	// 1. Not readonly
	// 2. At least a ticket category selected
	// 3. At least some position quantities entered
	// 4. Basic invoice details filled (invoiceNumber, dates, billing periods)
	// 5. No position validation errors (e.g., quantities below minimum)
	const canAutosave =
		!readonly &&
		!!selectedTicketCategory &&
		hasPositionsWithQuantities &&
		hasBasicInvoiceDetails &&
		!positionValidation.hasErrors;

	return canAutosave;
};
